import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import Toast, { ToastType } from "../components/Toast";
import { apiService } from "../services/api";
import { FiSave, FiSettings } from "react-icons/fi";

interface MaintenanceSettingsData {
  id: number;
  message_en: string;
  message_fr: string;
  is_enabled: boolean;
  created_at: string;
  created_by: number | null;
  created_by_email: string | null;
  modified_at: string;
  modified_by: number | null;
  modified_by_email: string | null;
}

const MaintenanceSettings: React.FC = () => {
  const [settings, setSettings] = useState<MaintenanceSettingsData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getMaintenanceSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      setToast({
        message: "Failed to load maintenance settings",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof MaintenanceSettingsData, value: any) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const response = await apiService.updateMaintenanceSettings(settings);
      if (response.success) {
        setToast({ message: "Settings saved successfully", type: "success" });
        fetchSettings(); // Refresh to get updated timestamps
      } else {
        setToast({
          message: response.message || "Failed to save settings",
          type: "error",
        });
      }
    } catch (error) {
      setToast({ message: "Failed to save settings", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!settings) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">
            Failed to load settings
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Maintenance Settings
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage maintenance mode and messages displayed to users
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            <FiSave className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {/* Content */}
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          {/* Enable Toggle */}
          <div className="mb-6 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white">
                  <FiSettings className="mr-2 inline h-5 w-5" />
                  Maintenance Mode
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  When enabled, users will see the maintenance message
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={settings.is_enabled}
                  onChange={(e) => updateField("is_enabled", e.target.checked)}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-300 dark:border-gray-600 dark:bg-gray-700"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {settings.is_enabled ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
          </div>

          {/* Messages */}
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maintenance Message (English)
              </label>
              <textarea
                value={settings.message_en}
                onChange={(e) => updateField("message_en", e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                placeholder="Enter maintenance message in English..."
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maintenance Message (French)
              </label>
              <textarea
                value={settings.message_fr}
                onChange={(e) => updateField("message_fr", e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                placeholder="Enter maintenance message in French..."
              />
            </div>
          </div>

          {/* Audit Info */}
          <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Audit Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <span className="font-medium">Created:</span>{" "}
                {new Date(settings.created_at).toLocaleString()}
                {settings.created_by_email && (
                  <span className="ml-2">by {settings.created_by_email}</span>
                )}
              </div>
              <div>
                <span className="font-medium">Modified:</span>{" "}
                {new Date(settings.modified_at).toLocaleString()}
                {settings.modified_by_email && (
                  <span className="ml-2">by {settings.modified_by_email}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default MaintenanceSettings;
